import { useState } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Send, UserPlus, Car, FileText } from "lucide-react";

export default function DevisSender({ onSent }) {
  const [mode, setMode] = useState("existing"); // existing | new
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [saveClient, setSaveClient] = useState(true);
  const [link, setLink] = useState("");
  const [montant, setMontant] = useState("");
  const [sending, setSending] = useState(false);

  // Pré-dossier
  const [createPredossier, setCreatePredossier] = useState(true);
  const [immat, setImmat] = useState("");
  const [marque, setMarque] = useState("");
  const [modele, setModele] = useState("");
  const [couleur, setCouleur] = useState("");
  const [typeInter, setTypeInter] = useState("carrosserie");
  const [description, setDescription] = useState("");
  const [note, setNote] = useState("");

  // Autocomplete véhicule (base véhicules importée)
  const [vResults, setVResults] = useState([]);
  const [vSearching, setVSearching] = useState(false);

  const doSearch = async (q) => {
    setSearch(q);
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await api.get("/clients", { params: { search: q, limit: 20 } });
      setResults(res.data || []);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const searchVehicles = async (q) => {
    const upper = q.toUpperCase();
    setImmat(upper);
    if (upper.length < 2) { setVResults([]); return; }
    setVSearching(true);
    try {
      const res = await api.get("/vehicules-db", { params: { search: upper, limit: 8 } });
      setVResults(res.data?.vehicules || []);
    } catch {
      setVResults([]);
    } finally {
      setVSearching(false);
    }
  };

  const selectVehicle = (v) => {
    setImmat(v.immatriculation || "");
    setMarque(v.marque || "");
    setModele(v.modele || "");
    setCouleur(v.couleur || "");
    setVResults([]);
    // Aide : pré-remplir le client si présent en base véhicule et mode "nouveau"
    if (v.client_nom && mode === "new" && !newName) setNewName(v.client_nom);
  };

  const resetAll = () => {
    setLink(""); setMontant(""); setSelected(null); setSearch(""); setResults([]);
    setNewName(""); setNewPhone("");
    setImmat(""); setMarque(""); setModele(""); setCouleur(""); setTypeInter("carrosserie");
    setDescription(""); setNote(""); setVResults([]);
  };

  const handleSend = async () => {
    const telephone = mode === "existing" ? selected?.telephone : newPhone.trim();
    const client_name = mode === "existing"
      ? `${selected?.prenom || ""} ${selected?.nom || ""}`.trim()
      : newName.trim();
    if (!telephone) { toast.error("Sélectionnez ou saisissez un numéro de téléphone"); return; }
    if (!link.trim()) { toast.error("Saisissez le lien du devis"); return; }
    if (createPredossier && !immat.trim()) { toast.error("Renseignez l'immatriculation pour créer le pré-dossier"); return; }
    setSending(true);
    try {
      const res = await api.post("/sms/send-devis-direct", {
        telephone,
        client_name,
        link: link.trim(),
        montant: montant.trim(),
        client_id: mode === "existing" ? selected?.id : null,
        save_client: mode === "new" ? saveClient : false,
        create_predossier: createPredossier,
        immatriculation: immat.trim(),
        marque: marque.trim(),
        modele: modele.trim(),
        couleur: couleur.trim(),
        type_intervention: typeInter,
        description: description.trim(),
        note: note.trim(),
      });
      if (res.data.success) {
        toast.success(createPredossier
          ? "Devis envoyé + pré-dossier créé dans « Devis envoyés » ✅"
          : "Devis envoyé par SMS ✅");
        resetAll();
        if (onSent) onSent();
      } else if (res.data.predossier_id) {
        toast.warning("SMS non envoyé, mais le pré-dossier a bien été créé dans « Devis envoyés »");
        resetAll();
        if (onSent) onSent();
      } else {
        toast.error(res.data.message || "Échec de l'envoi");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="devis-sender">
      {/* Client */}
      <div className="flex gap-2">
        <Button variant={mode === "existing" ? "default" : "outline"} className={mode === "existing" ? "bg-slate-900" : ""} onClick={() => { setMode("existing"); setNewName(""); setNewPhone(""); }} data-testid="mode-existing-btn">
          <Search className="h-4 w-4 mr-2" /> Client existant
        </Button>
        <Button variant={mode === "new" ? "default" : "outline"} className={mode === "new" ? "bg-slate-900" : ""} onClick={() => { setMode("new"); setSelected(null); setSearch(""); setResults([]); }} data-testid="mode-new-btn">
          <UserPlus className="h-4 w-4 mr-2" /> Nouveau client
        </Button>
      </div>

      {mode === "existing" ? (
        <div className="space-y-2">
          <Label className="text-xs">Rechercher un client</Label>
          <Input placeholder="Nom, prénom ou téléphone..." value={search} onChange={(e) => doSearch(e.target.value)} data-testid="devis-client-search" />
          {searching && <p className="text-xs text-slate-400">Recherche...</p>}
          {results.length > 0 && !selected && (
            <div className="border rounded-lg divide-y max-h-40 overflow-y-auto">
              {results.map((c) => (
                <button key={c.id} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm" onClick={() => { setSelected(c); setResults([]); }} data-testid="client-result">
                  <span className="font-medium">{c.nom} {c.prenom}</span> <span className="text-slate-400">— {c.telephone || "pas de tél."}</span>
                </button>
              ))}
            </div>
          )}
          {selected && (
            <div className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-sm" data-testid="selected-client">
              <span><span className="font-medium">{selected.nom} {selected.prenom}</span> — {selected.telephone || "pas de tél."}</span>
              <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>Changer</Button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Nom du client</Label>
            <Input placeholder="Ex: Dupont Jean" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="devis-new-name" />
          </div>
          <div>
            <Label className="text-xs">Téléphone *</Label>
            <Input placeholder="06 12 34 56 78" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} data-testid="devis-new-phone" />
          </div>
          <label className="col-span-2 flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={saveClient} onChange={(e) => setSaveClient(e.target.checked)} data-testid="save-client-checkbox" />
            Enregistrer ce client dans la base
          </label>
        </div>
      )}

      {/* Devis */}
      <div className="border-t pt-3 space-y-3">
        <div>
          <Label className="text-xs">Lien du devis * (Google Drive, etc.)</Label>
          <Input placeholder="https://drive.google.com/..." value={link} onChange={(e) => setLink(e.target.value)} data-testid="devis-link" />
        </div>
        <div>
          <Label className="text-xs">Montant (€) — optionnel</Label>
          <Input placeholder="Ex: 450" value={montant} onChange={(e) => setMontant(e.target.value)} data-testid="devis-montant" />
        </div>
      </div>

      {/* Case pré-dossier */}
      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 cursor-pointer">
        <Checkbox checked={createPredossier} onCheckedChange={(v) => setCreatePredossier(!!v)} data-testid="create-predossier-checkbox" />
        <FileText className="h-4 w-4 text-purple-600" />
        Créer un pré-dossier dans « Devis envoyés »
      </label>

      {/* Infos véhicule pour le pré-dossier */}
      {createPredossier && (
        <div className="border rounded-lg p-3 space-y-3 bg-slate-50/60" data-testid="predossier-fields">
          <p className="text-xs font-semibold text-slate-600 flex items-center gap-1"><Car className="h-3.5 w-3.5" /> Informations véhicule</p>
          <div className="grid grid-cols-3 gap-2">
            <div className="relative">
              <Label className="text-xs">Immatriculation * <Search className="h-3 w-3 inline text-slate-400" /></Label>
              <Input placeholder="AA-123-BB" value={immat} onChange={(e) => searchVehicles(e.target.value)} className="uppercase text-sm" data-testid="predossier-immat" />
              {vSearching && <p className="text-[10px] text-slate-400">Recherche...</p>}
              {vResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {vResults.map((v) => (
                    <button key={v.id} className="w-full text-left px-2 py-1.5 hover:bg-slate-50 border-b last:border-b-0" onClick={() => selectVehicle(v)} data-testid="vehicle-result">
                      <p className="font-mono font-bold text-xs">{v.immatriculation}</p>
                      <p className="text-[10px] text-slate-500">{v.marque} {v.modele}{v.client_nom ? ` • ${v.client_nom}` : ""}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Marque</Label>
              <Input value={marque} onChange={(e) => setMarque(e.target.value)} className="text-sm" data-testid="predossier-marque" />
            </div>
            <div>
              <Label className="text-xs">Modèle</Label>
              <Input value={modele} onChange={(e) => setModele(e.target.value)} className="text-sm" data-testid="predossier-modele" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Couleur</Label>
              <Input value={couleur} onChange={(e) => setCouleur(e.target.value)} className="text-sm" data-testid="predossier-couleur" />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={typeInter} onValueChange={setTypeInter}>
                <SelectTrigger className="text-sm" data-testid="predossier-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="carrosserie">Carrosserie</SelectItem>
                  <SelectItem value="mecanique">Mécanique</SelectItem>
                  <SelectItem value="peinture">Peinture divers</SelectItem>
                  <SelectItem value="pare_brise">Pare-brise</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Description des travaux</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="text-sm min-h-[60px]" data-testid="predossier-description" />
          </div>
          <div>
            <Label className="text-xs">Note interne</Label>
            <Textarea placeholder="Note libre (ex: relancer sous 1 semaine)" value={note} onChange={(e) => setNote(e.target.value)} className="text-sm min-h-[50px]" data-testid="predossier-note" />
          </div>
        </div>
      )}

      <p className="text-xs text-slate-400">Le message SMS est configurable dans Réglages → Modèles de SMS → Devis.</p>

      <Button onClick={handleSend} disabled={sending} className="w-full bg-red-600 hover:bg-red-700" data-testid="send-devis-direct-btn">
        <Send className="h-4 w-4 mr-2" /> {sending ? "Envoi..." : "Envoyer le devis"}
      </Button>
    </div>
  );
}
